import { trackConfig, type IFramework } from '@/framework';
import { TextMeasurer } from '@/utils/text-kit';
import {
    LayerType,
    TextWritingType,
    type ILayerProps,
    type IVectorLayerProps,
    type VectorTextInfo,
} from '@/version-types';
import { pushHistory, updateLayer } from '../utils';

export interface ITextSpacing {
    /**
     * 设置行间距
     *
     * @param layerID - 图层 ID
     * @param value - 行间距（注意：该值为底层数据）
     * @param options - 可选；配置选项；`options.writeHistory` 是否写入历史记录
     * @returns
     */
    setLineSpacing(layerID: string, value: number, options?: { writeHistory: boolean }): ILayerProps | void;

    /**
     * 设置字间距
     *
     * @param layerID - 图层 ID
     * @param value - 字间距（注意：该值为底层数据）
     * @param options - 可选；配置选项；`options.writeHistory` 是否写入历史记录
     */
    setLetterSpacing(layerID: string, value: number, options?: { writeHistory?: boolean }): ILayerProps | void;
}

export class TextSpacing implements ITextSpacing {
    private prevLayer: ILayerProps | null = null;

    constructor(private framework: IFramework) {}

    private calcLayout(layer: ILayerProps) {
        const {
            width: layerWidth,
            height: layerHeight,
            __proactiveWidth: proactiveWidth,
            __proactiveHeight: proactiveHeight,
        } = layer;
        const layerData = layer.data as IVectorLayerProps;
        const textInfo = layerData.textInfo as VectorTextInfo;

        const { writingMode, lineSpacing } = textInfo;
        const isHorizontal = writingMode === TextWritingType.HORIZONTAL_TB;

        const textSize = TextMeasurer.measure(layer, {
            keepConstantWidth: isHorizontal,
            keepConstantHeight: !isHorizontal,
            preservePadding: textInfo.padding,
        });

        const minWidth = proactiveWidth || layerWidth;
        const minHeight = proactiveHeight || layerHeight;

        let nextLayerWidth = layerWidth;
        let nextLayerHeight = layerHeight;

        if (isHorizontal) {
            nextLayerHeight = textSize.height <= minHeight ? minHeight : textSize.height;
        } else {
            nextLayerWidth = textSize.width <= minWidth ? minWidth : textSize.width;
        }

        // 行间距调整修正: 当行间距小于字号时，图层尺寸即为最小尺寸
        if (lineSpacing < 1) {
            if (isHorizontal) {
                if (nextLayerHeight > minHeight) nextLayerHeight = minHeight;
            } else {
                if (nextLayerWidth > minWidth) nextLayerWidth = minWidth;
            }
        }

        return {
            width: nextLayerWidth,
            height: nextLayerHeight,
        };
    }

    private set(
        layerID: string,
        kind: 'lineSpacing' | 'letterSpacing',
        value: number,
        options = { writeHistory: true },
    ) {
        const prevLayer = (() => {
            if (this.prevLayer) return this.prevLayer;
            this.prevLayer = this.framework.getLayer(layerID, true);
            return this.prevLayer;
        })();
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const layerData = prevLayer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        if (!textInfo) return;

        const { writeHistory } = options;

        if (writeHistory) {
            const nextLayer = this.framework.getLayer(layerID, true);

            pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER][kind]);
            this.prevLayer = null;

            return nextLayer;
        }

        const nextTextInfo: VectorTextInfo = { ...textInfo, [kind]: value };
        const nextLayerData: IVectorLayerProps = { ...layerData, textInfo: nextTextInfo };
        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const { width: nextLayerWidth, height: nextLayerHeight } = this.calcLayout(nextLayer);
        nextLayer.width = nextLayerWidth;
        nextLayer.height = nextLayerHeight;

        updateLayer(this.framework, nextLayer);

        return nextLayer;
    }

    setLetterSpacing(layerID: string, value: number, options = { writeHistory: true }) {
        return this.set(layerID, 'letterSpacing', value, options);
    }

    setLineSpacing(layerID: string, value: number, options = { writeHistory: true }) {
        return this.set(layerID, 'lineSpacing', value, options);
    }
}
